<mat-drawer-container class="drawer-container">
  <administration></administration>
  <div class="audit-log-container" (mouseleave)="onContainerMouseLeave()">
    <lib-cdw-ssrm-grid
      class="ag-theme-balham"
      viewKey="audit-log"
      [columnDefs]="columnDefs"
      [itemLabel]="itemLabel"
      [filterBarSettings]="filterBarSettings"
      [filterBarId]="'auditLogSearch'"
      [cdwSearchGridDataService]="auditLogDataService"
      [componentId]="'AuditLogComponent'"
      [initialSelectedFilterFields]="currentFilters"
      [initialPage]="initialPage"
      [fieldMask$]="fieldMask$"
      [showSelectAllBanner]="false"
      (filtersChange)="onAuditLogFiltersChanged($event)"
      (rowClicked)="onRowClicked($event)"
      (errorRetrievingData)="onGridLoadError($event)"
      #auditLogSearchGrid
    ></lib-cdw-ssrm-grid>
  </div>

  <!-- User hover card -->
  <div class="hover-card-overlay" *ngIf="hoverCardData$ | async as cardData" [style.top.px]="(hoverCardPosition$ | async)?.y" [style.left.px]="(hoverCardPosition$ | async)?.x">
    <div class="hover-card-content">
      <lib-user-profile-card [user]="cardData"></lib-user-profile-card>
    </div>
  </div>

  <lib-cdw-drawer
    drawerId="auditLogDrawer"
    [toolbarTitle]="drawerTitle"
    [toolbarSubtitle]="drawerSubtitle"
    [drawerWidth]="'350px'"
    [toolbarTitleBackground]="gray50"
    toolbarTitleFontSize="20px"
    toolbarSubtitleFontSize="16px"
    toolbarTitleFontWeight="800"
    toolbarSubtitleFontWeight="500"
    [toolbarTitleColor]="gray300"
    toolbarTitleFontFamily="Source Sans 3"
    toolbarSubtitleFontFamily="Source Sans 3"
    [hideHeaderBorder]="true"
  >
    <!-- User profile card — shared by standard and bulk-job drawers -->
    <div class="drawer-user-card" *ngIf="selectedUserData">
      <lib-user-profile-card [user]="selectedUserData" [hideContactInfo]="true"></lib-user-profile-card>
    </div>

    <!-- Bulk-job aggregation drawer (EDUS-2625, Figma 14618:238105) -->
    <ng-container *ngIf="isBulkJobDrawer && bulkJobData; else standardDrawer">
      <div class="bulk-job-drawer">
        <div class="bulk-job-divider-wrapper">
          <hr class="bulk-job-divider" />
        </div>

        <!-- Job Details section -->
        <section class="bulk-job-section">
          <h3 class="bulk-job-section-header">
            <lib-cdw-icon icon="description" size="20px"></lib-cdw-icon>
            <span>Job Details</span>
          </h3>
          <dl class="bulk-job-rows">
            <div class="bulk-job-row">
              <dt>Job Type:</dt>
              <dd>{{ bulkJobType }}</dd>
            </div>
            <div class="bulk-job-row">
              <dt>Date Created:</dt>
              <dd>{{ bulkJobCreated }}</dd>
            </div>
            <div class="bulk-job-row">
              <dt>Last Ran:</dt>
              <dd>{{ bulkJobLastRan }}</dd>
            </div>
            <div class="bulk-job-row">
              <dt>Created by:</dt>
              <dd>{{ bulkJobData.user?.name || '—' }}</dd>
            </div>
          </dl>
        </section>

        <div class="bulk-job-divider-wrapper">
          <hr class="bulk-job-divider" />
        </div>

        <!-- Aggregation statistics section — labels adapt to export, sync, and device-update jobs -->
        <section class="bulk-job-section">
          <h3 class="bulk-job-section-header">
            <lib-cdw-icon [icon]="isExportJob ? 'file_download' : isSyncJob ? 'sync' : 'laptop'" size="20px"></lib-cdw-icon>
            <span>{{ isExportJob ? 'Export Details' : isSyncJob ? 'Sync Details' : 'Device Details' }}</span>
          </h3>
          <!-- EDUS-3090: a cancel stops the workflow before the stats step runs -->
          <div
            *ngIf="bulkJobData.cancelled"
            class="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-gray-700"
            data-testid="audit-log-cancelled-notice"
          >
            <lib-cdw-icon icon="info" class="mt-0.5 shrink-0 text-blue-600"></lib-cdw-icon>
            <p>
              This job was cancelled while in progress<ng-container *ngIf="!bulkJobData.aggregationStatistics">, so its totals weren't recorded</ng-container>.
            </p>
          </div>
          <ng-container *ngIf="!bulkJobData.cancelled || bulkJobData.aggregationStatistics">
          <div class="sync-stats-grid" *ngIf="isSyncJob; else standardAggregation">
            <div class="sync-stat-card">
              <lib-cdw-icon icon="laptop" size="24px" [customColor]="ctaBlue"></lib-cdw-icon>
              <span class="sync-stat-count">{{ bulkJobData.aggregationStatistics?.devicesSynced ?? 0 }}</span>
              <span class="sync-stat-label">Devices</span>
            </div>
            <div class="sync-stat-card">
              <lib-cdw-icon icon="people" size="24px" [customColor]="ctaBlue"></lib-cdw-icon>
              <span class="sync-stat-count">{{ bulkJobData.aggregationStatistics?.usersSynced ?? 0 }}</span>
              <span class="sync-stat-label">Users</span>
            </div>
            <div class="sync-stat-card">
              <lib-cdw-icon icon="account_tree" size="24px" [customColor]="ctaBlue"></lib-cdw-icon>
              <span class="sync-stat-count">{{ bulkJobData.aggregationStatistics?.groupingsSynced ?? 0 }}</span>
              <span class="sync-stat-label">Groups / OUs</span>
            </div>
          </div>
          <ng-template #standardAggregation>
            <dl class="bulk-job-rows">
              <div class="bulk-job-row">
                <dt>Selected:</dt>
                <dd>{{ selectedCount }}</dd>
              </div>
              <div class="bulk-job-row">
                <dt>Executions Successful:</dt>
                <dd>{{ bulkJobData.aggregationStatistics?.totalSucceeded ?? 0 }}</dd>
              </div>
              <div class="bulk-job-row">
                <dt>Executions Failed:</dt>
                <dd>{{ bulkJobData.aggregationStatistics?.totalFailed ?? 0 }}</dd>
              </div>
              <!-- EDUS-2625: only actions with eligibility rules record skips -->
              <div class="bulk-job-row" *ngIf="bulkJobData.skippedCount != null">
                <dt>Skipped Devices:</dt>
                <dd>{{ bulkJobData.skippedCount }}</dd>
              </div>
            </dl>
          </ng-template>
          </ng-container>
        </section>

        <!-- Resources Generated section (EDUS-2668) — hidden for non-export bulk jobs -->
        <ng-container *ngIf="exportOutputUrl as outputUrl">
          <div class="bulk-job-divider-wrapper">
            <hr class="bulk-job-divider" />
          </div>

          <section class="bulk-job-section">
            <h3 class="bulk-job-section-header">
              <lib-cdw-icon icon="content_copy" size="20px"></lib-cdw-icon>
              <span>Resources Generated</span>
            </h3>
            <a class="bulk-job-sheets-link" [href]="outputUrl" target="_blank" rel="noopener">
              <lib-cdw-icon icon="open_in_new" size="20px"></lib-cdw-icon>
              <span>{{ exportOutputLabel }}</span>
            </a>
          </section>
        </ng-container>

        <!-- Footer buttons -->
        <div class="bulk-job-footer">
          <button type="button" class="btn-close" (click)="closeDrawer()">Close</button>
          <!-- EDUS-2668: only show Details when the target page has asset changes to render -->
          <button *ngIf="showJobDetailsButton" type="button" class="btn-details" (click)="onViewJobDetailsClick()">
            Details
          </button>
        </div>
      </div>
    </ng-container>

    <!-- Existing per-user / single-event audit log drawer -->
    <ng-template #standardDrawer>
      <lib-cdw-drawer-content
        drawerContentId="auditLogDrawerContent"
        [contentTitle]="''"
        [noContentMessage]="'No audit logs found'"
        [drawerContents]="drawerContents"
        [hasSubtitle]="false"
        [disableEdit]="true"
        [disableDelete]="true"
      >
      </lib-cdw-drawer-content>
    </ng-template>
  </lib-cdw-drawer>
</mat-drawer-container>
